import axios from 'axios';
import { toast } from 'react-toastify';
import { STORAGE_BASE } from '../config';
import { widgetStyleCss } from '../components/widgetStyle';

// Shared official-invoice builder — the SAME rendering used by the admin
// preview and by the customer PDF, so the printed invoice always matches
// the template designed in the admin panel (incl. full frame settings).
// d: { orderId, productName, productCategory, basePrice, total, gatewayLabel,
//      paymentRef, date, phone }

const SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};

// مبلغ نهایی = (جمع اقلام + هزینه ارسال + مالیات + کارمزد) − تخفیف
// shippingNet = هزینه ارسال پس از تخفیف ارسال
export function calcTotal(d) {
  const num = v => Number(v || 0);
  const shippingNet = Math.max(0, num(d.shippingCost) - num(d.shippingDiscount));
  return Math.max(0, Math.round(
    num(d.subtotal ?? d.basePrice) + shippingNet + num(d.tax) + num(d.fee) - num(d.discount)
  ));
}

export function applyShortcodes(html, d) {
  const fa = n => Number(n || 0).toLocaleString('fa-IR');
  const total = calcTotal(d);
  const productName = d.productName
    || (Array.isArray(d.items) && d.items.length ? d.items.join('، ') : '');
  return String(html || '')
    .replace(/{{order_id}}/g, d.orderId ?? '-')
    .replace(/{{product_name}}/g, productName || '')
    .replace(/{{product_category}}/g, d.productCategory || '')
    .replace(/{{product_price}}/g, fa(d.basePrice ?? d.subtotal ?? d.total))
    .replace(/{{product_image}}/g, d.productImage || '')
    .replace(/{{subtotal}}/g, fa(d.subtotal ?? d.basePrice ?? d.total))
    .replace(/{{discount_code}}/g, d.discountCode || '—')
    .replace(/{{discount}}/g, fa(d.discount))
    .replace(/{{fee_percent}}/g, d.feePercent != null && d.feePercent !== '' ? d.feePercent : '0')
    .replace(/{{fee}}/g, fa(d.fee))
    .replace(/{{tax_percent}}/g, d.taxPercent != null && d.taxPercent !== '' ? d.taxPercent : '0')
    .replace(/{{tax}}/g, fa(d.tax))
    .replace(/{{shipping_method}}/g, d.shippingMethodLabel || d.shippingMethod || '—')
    .replace(/{{shipping_cost}}/g, fa(d.shippingCost))
    .replace(/{{shipping_discount}}/g, fa(d.shippingDiscount))
    .replace(/{{total}}/g, fa(total))
    .replace(/{{date}}/g, d.date || new Date().toLocaleDateString('fa-IR'))
    .replace(/{{user_phone}}/g, d.phone || d.userPhone || localStorage.getItem('user_phone') || localStorage.getItem('customer_phone') || '-')
    .replace(/{{user_name}}/g, d.userName || '-')
    .replace(/{{user_email}}/g, d.userEmail || '-')
    .replace(/{{user_national_code}}/g, d.userNationalCode || '-')
    .replace(/{{user_province}}/g, d.userProvince || '-')
    .replace(/{{user_city}}/g, d.userCity || '-')
    .replace(/{{user_address}}/g, d.userAddress || '-')
    .replace(/{{user_postal_code}}/g, d.userPostalCode || '-')
    .replace(/{{gateway_ref}}/g, d.paymentRef || '-')
    .replace(/{{gateway}}/g, d.gatewayLabel || '');
}

// Full frame css (same conventions as page widgets in homeRows:
// 4-direction padding/margin, per-corner radius, border size+color, shadow).
function frameCss(f, extraMarginBottom = '8px') {
  f = f || {};
  const pad = (f.paddingT ?? f.padding ?? 0) + 'px ' + (f.paddingR ?? f.padding ?? 0) + 'px '
    + (f.paddingB ?? f.padding ?? 0) + 'px ' + (f.paddingL ?? f.padding ?? 0) + 'px';
  const hasMargin = f.marginT || f.marginR || f.marginB || f.marginL;
  const mar = hasMargin
    ? (f.marginT || 0) + 'px ' + (f.marginR || 0) + 'px ' + (f.marginB || 0) + 'px ' + (f.marginL || 0) + 'px'
    : '0 0 ' + extraMarginBottom + ' 0';
  const tl = f.radiusTL ?? f.radius ?? 0, tr = f.radiusTR ?? f.radius ?? 0;
  const br = f.radiusBR ?? f.radius ?? 0, bl = f.radiusBL ?? f.radius ?? 0;
  const border = f.border || ((f.borderSize || f.borderColor) ? (f.borderSize ?? 1) + 'px solid ' + (f.borderColor || '#E4E4E7') : '');
  const shadow = SHADOWS[f.shadow] || '';
  return { pad, mar, radius: (tl || tr || br || bl) ? `${tl}px ${tr}px ${br}px ${bl}px` : '0px', border, shadow, bg: f.bg || 'transparent' };
}

export function buildInvoiceHtml(widgets, d) {
  const parts = (Array.isArray(widgets) ? widgets : [])
    .filter(w => w && w.enabled !== false)
    .map(w => {
      const s = w.settings || {};
      const f = s.frame || {};
      const uni = widgetStyleCss(s.style);
      const uniAttr = uni ? `;${uni}` : '';
      if (w.type === 'text') {
        const c = frameCss(f);
        return `<div style="padding:${c.pad};margin:${c.mar};background:${s.bg && s.bg !== 'transparent' ? s.bg : c.bg};border-radius:${c.radius};${c.border ? `border:${c.border};` : ''}${c.shadow ? `box-shadow:${c.shadow};` : ''}text-align:${s.align || 'right'};color:${s.color || '#18181B'};font-size:${s.fontSize || 14}px${uniAttr}">${applyShortcodes(s.content || '', d)}</div>`;
      }
      if (w.type === 'divider') {
        return `<div style="height:${s.height || 2}px;background:${s.color || '#FFE082'};margin:${f.padding || 0}px 0;border-radius:4px${uniAttr}"></div>`;
      }
      if (w.type === 'hero') {
        const c = frameCss(f, '16px');
        return `<div style="padding:${c.pad};margin:${c.mar};background:${c.bg === 'transparent' ? (s.bg || '#fff') : c.bg};border-radius:${c.radius};${c.border ? `border:${c.border};` : ''}${c.shadow ? `box-shadow:${c.shadow};` : ''}text-align:${s.align || 'right'}${uniAttr}"><h2 style="margin:0 0 8px;color:${s.color || '#18181B'};font-size:${s.fontSize || 28}px">${applyShortcodes(s.title || '', d)}</h2><p style="margin:0;color:${s.descColor || '#78716c'};font-size:${s.fontSize ? Math.round(s.fontSize * 0.6) : 16}px">${applyShortcodes(s.desc || '', d)}</p></div>`;
      }
      if (w.type === 'table') {
        const headers = (s.headers || '').split(',').map(h => h.trim()).filter(Boolean);
        const nCols = Math.max(headers.length, 1);
        // Every row ALWAYS has exactly nCols horizontal cells:
        // - pipe format: cells split by | (lines are just for readability)
        // - legacy comma format: chunked by column count
        const rawRows = String(s.rows || '');
        let cells;
        if (rawRows.includes('|')) {
          cells = [];
          rawRows.split('\n').forEach(l => {
            if (l.trim() === '') return;
            l.split('|').forEach(c => cells.push(c.trim()));
          });
        } else {
          cells = rawRows.split(/[,\n]/).map(r => r.trim()).filter(c => c !== '');
        }
        const grid = [];
        for (let i = 0; i < cells.length; i += nCols) grid.push(cells.slice(i, i + nCols));
        const bc = s.borderColor || '#FFE082';
        const hbg = s.headerBg || '#FDFBF7';
        const hcolor = s.headerColor || s.color || '#18181B';
        const bcolor = s.bodyColor || s.color || '#18181B';
        const rb = s.rowBorder || '#F4F4F5';
        const cell = c => `<td style="padding:10px 12px">${applyShortcodes(c || '', d)}</td>`;
        return `<div style="margin:${frameCss(f).mar}${uniAttr}"><table style="width:100%;border-collapse:collapse;border:1.5px solid ${bc};border-radius:12px;overflow:hidden;font-size:${s.fontSize || 13}px;color:${bcolor};text-align:right"><thead><tr style="background:${hbg};font-weight:700">`
          + headers.map(h => `<th style="padding:10px 12px;border-bottom:1.5px solid ${bc};color:${hcolor}">${applyShortcodes(h, d)}</th>`).join('')
          + `</tr></thead><tbody>`
          + grid.map(cells => {
              const padded = cells.concat(Array(Math.max(0, nCols - cells.length)).fill(''));
              return `<tr style="border-bottom:1px solid ${rb}">${padded.slice(0, nCols).map(cell).join('')}</tr>`;
            }).join('')
          + `</tbody></table></div>`;
      }
      if (w.type === 'icon_text') {
        const c = frameCss(f);
        const icon = s.icon || '';
        const isEmoji = /\p{Extended_Pictographic}/u.test(icon);
        return `<div style="padding:${c.pad};margin:${c.mar};background:${c.bg === 'transparent' ? (s.bg || '#fff') : c.bg};border-radius:${c.radius};${c.border ? `border:${c.border};` : `border:${f.borderSize || 1}px solid ${f.borderColor || '#FFE082'};`}display:flex;gap:10px;align-items:center;text-align:${s.align || 'right'}${uniAttr}">`
          + (isEmoji ? `<span style="font-size:1.4rem;flex-shrink:0">${icon}</span>` : '')
          + `<div><div style="font-weight:700;color:${s.color || '#18181B'};font-size:${s.fontSize || 16}px">${applyShortcodes(s.title || '', d)}</div></div></div>`;
      }
      if (w.type === 'image') {
        const imgPath = applyShortcodes((s.image || '').trim(), d);
        const src = imgPath ? (imgPath.startsWith('http') ? imgPath : STORAGE_BASE + '/' + imgPath.replace(/^\//, '')) : '';
        if (!src) return '';
        const imgW = s.imgWidth || '100%';
        const imgH = s.imgHeight || '260px';
        return `<div style="margin-bottom:8px;text-align:center${uniAttr}"><img src="${src}" alt="" style="width:${imgW};height:${imgH};max-width:100%;border-radius:${(f.radius ?? 12)}px;object-fit:cover;border:${f.borderSize || 1}px solid ${f.borderColor || '#FFE082'};background:#F4F4F5" /></div>`;
      }
      return '';
    }).join('');
  return `<div style="font-family:'IRANSans',sans-serif;border:2px solid #18181B;border-radius:16px;padding:24px;background:#fff;direction:rtl;text-align:right;max-width:720px;margin:auto;box-shadow:0 8px 30px rgba(0,0,0,0.08)">${parts}</div>`;
}

export const PREVIEW_SAMPLE = {
  orderId: 'ORD-9876', productName: 'انگشتر طلا ۱۸ عیار', productCategory: 'انگشتر و حلقه',
  basePrice: 4850000, total: 4850000, gatewayLabel: 'کیف پول', paymentRef: 'REF-12345',
  date: new Date().toLocaleDateString('fa-IR'), phone: '۰۹۱۲۱۲۳۴۵۶۷',
  userName: 'علی رضایی', userEmail: 'ali@example.com', userNationalCode: '۰۰۲۱۲۳۴۵۶۷',
  userProvince: 'تهران', userCity: 'تهران', userAddress: 'خیابان ولیعصر، پلاک ۱۰',
  userPostalCode: '۱۹۳۹۵۳۶۴۷۱',
  items: ['انگشتر طلا ۱۸ عیار × ۱'],
  subtotal: 5000000, discountCode: 'GOLD10', discount: 500000,
  feePercent: 2, fee: 90000, taxPercent: 10, tax: 499000,
  shippingMethod: 'پست پیشتاز', shippingMethodLabel: 'پست پیشتاز', shippingCost: 45000, shippingDiscount: 0,
};

function fallbackBody(d) {
  const fa = n => Number(n || 0).toLocaleString('fa-IR');
  const productName = d.productName
    || (Array.isArray(d.items) && d.items.length ? d.items.join('، ') : '');
  const rows = [
    `<p><b>محصول:</b> ${productName || '-'}</p>`,
    d.productCategory ? `<p><b>دسته‌بندی:</b> ${d.productCategory}</p>` : '',
    `<p><b>خریدار:</b> ${d.userName || '-'} — ${d.phone || d.userPhone || localStorage.getItem('user_phone') || localStorage.getItem('customer_phone') || '-'}</p>`,
    `<p><b>جمع اقلام:</b> ${fa(d.subtotal ?? d.basePrice ?? d.total)} تومان</p>`,
  ];
  if (d.userAddress || d.userCity) rows.push(`<p><b>نشانی:</b> ${[d.userProvince, d.userCity, d.userAddress, d.userPostalCode].filter(Boolean).join('، ')}</p>`);
  if (Number(d.discount) > 0) rows.push(`<p><b>تخفیف${d.discountCode ? ` (${d.discountCode})` : ''}:</b> ${fa(d.discount)} تومان</p>`);
  if (Number(d.fee) > 0) rows.push(`<p><b>کارمزد (${d.feePercent || 0}٪):</b> ${fa(d.fee)} تومان</p>`);
  if (Number(d.tax) > 0) rows.push(`<p><b>مالیات (${d.taxPercent || 0}٪):</b> ${fa(d.tax)} تومان</p>`);
  if (Number(d.shippingCost) > 0 || d.shippingMethod) rows.push(`<p><b>ارسال (${d.shippingMethodLabel || d.shippingMethod || '—'}):</b> ${fa(d.shippingCost)} تومان${Number(d.shippingDiscount) > 0 ? ` (تخفیف ارسال: ${fa(d.shippingDiscount)})` : ''}</p>`);
  rows.push(
    `<p style="font-weight:700;font-size:1.1rem"><b>مبلغ نهایی:</b> ${fa(calcTotal(d))} تومان</p>`,
    d.gatewayLabel ? `<p><b>روش پرداخت:</b> ${d.gatewayLabel}</p>` : '',
    d.paymentRef ? `<p><b>شماره پیگیری درگاه:</b> ${d.paymentRef}</p>` : '',
    `<p><b>تاریخ:</b> ${d.date || new Date().toLocaleDateString('fa-IR')}</p>`,
    `<p><b>وضعیت پرداخت:</b> پرداخت شده</p>`,
  );
  return `<h2 style="margin-top:0">فاکتور رسمی سفارش #${d.orderId}</h2><hr/>` + rows.join('');
}

// MUST be called synchronously inside the click handler (popup blockers
// cancel window.open if it happens after an await).
export function openInvoiceWindow() {
  try {
    const w = window.open('', '_blank', 'width=850,height=900');
    if (!w) toast.error('پاپ‌آپ مسدود است؛ لطفاً پاپ‌آپ را برای این سایت مجاز کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    else w.document.write('<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>در حال ساخت فاکتور...</title></head><body style="font-family:sans-serif;padding:40px;text-align:center">در حال ساخت فاکتور...</body></html>');
    return w;
  } catch (e) {
    toast.error('باز کردن پنجره چاپ ممکن نشد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    return null;
  }
}

export async function printInvoiceInto(win, d) {
  try {
    let body = '';
    try {
      const r = await axios.get('/site-settings', {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const raw = r.data && r.data.invoice_template_widgets;
      if (raw) {
        const widgets = JSON.parse(raw);
        if (Array.isArray(widgets) && widgets.length) body = buildInvoiceHtml(widgets, d);
      }
    } catch { /* fallback below */ }
    if (!body) body = `<div style="font-family:'IRANSans',sans-serif;border:2px solid #18181B;border-radius:16px;padding:24px;background:#fff;direction:rtl;text-align:right;max-width:720px;margin:auto">${fallbackBody(d)}</div>`;
    const origin = window.location.origin;
    const fontCss = [400, 500, 700].map(wgt => {
      const file = wgt >= 700 ? 'IRANSans-Bold.woff2' : 'IRANSans-Reg.woff';
      return `@font-face{font-family:'IRANSans';src:url('${origin}/fonts/${file}') format('${file.endsWith('.woff2') ? 'woff2' : 'woff'}');font-weight:${wgt};font-style:normal;font-display:swap;}`;
    }).join('');
    win.document.open();
    win.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>فاکتور ${d.orderId}</title><style>${fontCss}body{font-family:'IRANSans','Vazir',sans-serif;background:#fff;margin:0;padding:24px;color:#18181B;line-height:1.8;}hr{border:none;border-top:1px solid #E4E4E7;margin:12px 0;}table{width:100%;border-collapse:collapse;}th,td{padding:10px 12px;text-align:right;border-bottom:1px solid #F4F4F5;}th{background:#F4F4F5;font-weight:700;}</style></head><body>${body}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch {} }, 600);
  } catch (e) {
    toast.error('ساخت فاکتور ممکن نشد: ' + (e.message || e), { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
  }
}

// Convenience: opens synchronously then fills (keeps popup allowed).
export function downloadInvoicePdf(d) {
  const win = openInvoiceWindow();
  if (win) printInvoiceInto(win, d);
  return win;
}
